import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { noop } from 'lodash';

import { dispatchSnackbarWarningEvent } from '@dreryk/react-components';

import { useElevenLabsTranscription } from '../transcription/useElevenLabsTranscription';
import type { UseTranscriptionResult } from '../transcription/useTranscription';
import { finalizeTrailingPartial, updateUtterances } from '../transcription/utterances';
import { translationKeys } from '../translations';
import type { TranscriptionErrorCode, Utterance } from '../types';

type AiAssistantTranscriptionContextValue = UseTranscriptionResult & {
   startSession: (...parameters: Parameters<UseTranscriptionResult['start']>) => Promise<boolean>;
   getTranscriptUtterances: () => Utterance[];
   clearTranscript: VoidFunction;
};

const aiAssistantTranscriptionContext = createContext<AiAssistantTranscriptionContextValue | null>(null);

export const useAiAssistantTranscriptionContext = (): AiAssistantTranscriptionContextValue => {
   const context = useContext(aiAssistantTranscriptionContext);

   if (context === null) {
      throw new Error('useAiAssistantTranscriptionContext cannot be used outside AiAssistantTranscriptionProvider');
   }
   return context;
};

const { Provider } = aiAssistantTranscriptionContext;

type AiAssistantTranscriptionProviderProps = { children: JSX.Element };

export const AiAssistantTranscriptionProvider = (props: AiAssistantTranscriptionProviderProps): JSX.Element => {
   const utterancesRef = useRef<Utterance[]>([]);
   const startFailedRef = useRef(false);

   const handleSpeakerTranscript = useCallback((speaker: string, text: string, isPartial: boolean) => {
      utterancesRef.current = updateUtterances(utterancesRef.current, speaker, text, isPartial);
   }, []);

   const handleStop = useCallback(() => {
      utterancesRef.current = finalizeTrailingPartial(utterancesRef.current);
   }, []);

   const handleError = useCallback((code: TranscriptionErrorCode) => {
      startFailedRef.current = true;

      if (code === 'MICROPHONE_ACCESS_DENIED') {
         dispatchSnackbarWarningEvent(translationKeys.faults.microphoneAccessDenied);
      }
   }, []);

   const options = {
      onPartial: noop,
      onFinal: noop,
      onSpeakerTranscript: handleSpeakerTranscript,
      onStop: handleStop,
      onError: handleError,
   };

   const transcription = useElevenLabsTranscription(options);

   const { start } = transcription;
   const startSession = useCallback(
      async (...parameters: Parameters<UseTranscriptionResult['start']>): Promise<boolean> => {
         startFailedRef.current = false;
         await start(...parameters);

         return !startFailedRef.current;
      },
      [start]
   );

   const getTranscriptUtterances = useCallback(() => utterancesRef.current, []);

   const clearTranscript = useCallback(() => {
      utterancesRef.current = [];
   }, []);

   const value = useMemo(
      () => ({
         ...transcription,
         startSession: startSession,
         getTranscriptUtterances: getTranscriptUtterances,
         clearTranscript: clearTranscript,
      }),
      [transcription, startSession, getTranscriptUtterances, clearTranscript]
   );

   return <Provider value={value}>{props.children}</Provider>;
};
